/**
 * MER-579: a section describes itself from the widgets inside it.
 *
 * AJ's preview puts a provenance string on the right of every section header:
 * "Disposal Tracker v11 · 5 KPIs", "AR Aging · 3,316 accounts", "Samsara
 * weekly feed · Delta only". That answers the question Seth kept asking --
 * where did this number come from -- without opening anything.
 *
 * It is DERIVED, never authored. Hand-writing 72 strings per customer is the
 * same pattern that produced the Safety & Fleet drift this ticket also fixes:
 * a hardcoded map keyed on a tab id that a rename silently invalidated, with
 * no test to catch it. Widgets already declare source.name, so the section can
 * read its own provenance and a new customer's dashboard describes itself with
 * zero authoring.
 *
 * Plain .mjs so node:test imports it with no build step.
 */

/**
 * MER-584: plain-English name for a canonical feed.
 *
 * The mockup's provenance reads "Disposal Tracker v11", which is DELTA'S OWN
 * TOOL. That is the one thing this must not be. AJ: "Every customer should see
 * the same thing. If they don't have a certain LOB then they wouldn't see
 * Resi." Naming one customer's tool would make their dashboard different in
 * KIND, not just in surface area -- Best Disposal has no Delta tools to name.
 *
 * So it names the FEED, from a map of OUR twelve canonical sources rather than
 * 72 hand-authored strings per customer. It changes when we add an entity, not
 * when we add a customer. Anything unmapped degrades to the source name with
 * its prefix stripped, so a new entity reads sensibly on day one.
 */
const FEED_LABEL = {
  "Meridian - KPI Summary": "KPI rollup",
  "Meridian - Service Events": "Work orders",
  "Meridian - Financial Health": "AR + collections",
  "Meridian - Route KPIs": "Route rollup",
  "Meridian - Assets": "Fleet telematics",
  "Meridian - Customers": "Customer master",
  "Meridian - AR Current": "AR aging",
  "Meridian - AR Transactions": "AR transactions",
  "Meridian - Top AR Accounts": "AR aging",
  "Meridian - Site Contracts": "Contracts",
  "Meridian - Contract Expirations": "Contracts",
  "Meridian - Audit Findings": "Data quality",
  // MER-551: the scale-house tickets themselves, not the KPI rollup over them.
  "Meridian - Disposal": "Disposal tickets",
};

/**
 * MER-836: a widget whose source is a Zoho-era query id names the feed that
 * query reads, not the query.
 *
 * Four CQT ids are still widget sources. Each is a Postgres view, and each
 * view's own FROM clause in 0043_tenant_scoped_cqt_views.sql says which
 * canonical table it reads; the migration repeats it in a `Source:` comment
 * above the view. That is where these four come from, not from a guess.
 *
 * Without this the Operations header read "CQT-23 + Route rollup, 2 tiles"
 * and only the three-or-more-sources collapse was keeping the Data Quality
 * ids off the page, which is luck rather than a rule. Anything else that
 * looks like a query id degrades to no name at all, so the header falls back
 * to its tile count rather than printing one.
 */
const CQT_FEED = {
  // view_cqt_14 Unmapped Billing Segments, view_cqt_15 Null LOB on Invoices,
  // view_cqt_16 Quarterly Advance Completeness: all FROM invoice.
  "CQT-14": "Invoices",
  "CQT-15": "Invoices",
  "CQT-16": "Invoices",
  // view_cqt_23 Driver Performance by LOB: FROM service_event.
  "CQT-23": "Work orders",
};

const LOOKS_LIKE_A_QUERY_ID = /^(?:CQT-\d+[A-Za-z]?|RPT-[A-Z0-9]+(?:-[A-Z0-9]+)*)$/i;

/** Plain-English feed name; falls back to the source with its prefix stripped. */
function shortSource(name) {
  const raw = String(name || "").trim();
  if (!raw) return "";
  const mapped = FEED_LABEL[raw] ?? CQT_FEED[raw.toUpperCase()];
  if (mapped) return mapped;
  if (LOOKS_LIKE_A_QUERY_ID.test(raw)) return "";
  return raw.replace(/^Meridian\s*-\s*/, "").trim();
}

/**
 * Build the right-hand provenance string for a section.
 *
 * @param {Array<{source?: {kind?: string, name?: string}}>} widgets
 * @returns {string} e.g. "KPI Summary · 5 KPIs", or "" when nothing is known
 */
export function sectionProvenance(widgets) {
  const list = Array.isArray(widgets) ? widgets : [];
  if (list.length === 0) return "";

  const names = [];
  for (const w of list) {
    const src = w && w.source;
    const name = shortSource(src && src.name);
    if (name && !names.includes(name)) names.push(name);
  }

  const count = `${list.length} ${list.length === 1 ? "tile" : "tiles"}`;
  if (names.length === 0) return count;
  // Two sources still fit; beyond that the list stops being readable and the
  // count of sources is the useful fact.
  const sources = names.length <= 2 ? names.join(" + ") : `${names.length} sources`;
  return `${sources} · ${count}`;
}
